import { ArrowRight, CircleCheck as CheckCircle2, Quote, Star } from 'lucide-react';
import { Link } from "@/components/common/Link";
export default function Testimonials() {
  const testimonials = [{
    id: "growth",
    eyebrow: "Revenue Growth",
    metric: "15–25% RevPAR Lift",
    metricDetail: "Fewer OTA Commission Leaks",
    quote: "Sunil changed how we handle OTAs. We stopped guessing and started checking compset pricing and pickup pace every single morning. Rates hold firm, and our net revenue is consistently up.",
    name: "Rajesh K.",
    role: "General Manager",
    property: "Heritage Boutique Hotel",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/boutique_hotel_revenue_success.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/boutique_hotel_revenue_success.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/boutique_hotel_revenue_success.png-wvc-sizes",
    rating: 5
  }, {
    id: "tech",
    eyebrow: "Distribution & Tech",
    metric: "Zero Booking Overlaps",
    metricDetail: "Fewer Booking Errors",
    quote: "Our channel manager and PMS were constantly falling out of sync, leading to double bookings on peak weekends. Sunil cleaned up the two-way mapping, fixed our room categories, and cut manual entry completely.",
    name: "Sarah M.",
    role: "Managing Owner",
    property: "Azure Ridge Resort Group",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/resort_tech_stack_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/resort_tech_stack_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/resort_tech_stack_optimization.webp-wvc-sizes",
    rating: 5
  }, {
    id: "audit",
    eyebrow: "Rate Parity Audit",
    metric: "Protected Direct Rates",
    metricDetail: "More Direct Bookings",
    quote: "We had no idea Agoda and B2B bedbanks were undercutting our front-desk rates by ₹800 every day. Sunil traced the rogue wholesalers, shut down the unapproved feeds, and brought real margin back to our direct engine.",
    name: "David L.",
    role: "Hospitality Investor",
    property: "Prime Portfolio Assets",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/investor_revenue_audit_results.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/investor_revenue_audit_results.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/investor_revenue_audit_results.webp-wvc-sizes",
    rating: 5
  }];
  return <section data-section-id="7739" id="clienttestimonials" className="bg-white text-foreground py-24 md:py-32 overflow-hidden border-t border-border isolate">
      <div className="container mx-auto max-w-7xl px-6">
        {/* Centered Eyebrow + H2 Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-4 px-3 py-1 rounded-lg bg-secondary/10 border border-secondary/20">
            Client Results
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight mb-6">
            Proven results for{" "}
            <span className="text-secondary italic font-serif">independent</span>{" "}
            hoteliers.
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Direct feedback from hotel owners and general managers who stopped relying on static rack rates and took back control of their distribution.
          </p>
        </div>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((item, i) => <div key={item.id} data-index={i} className="group relative flex flex-col justify-between bg-card border border-border rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-secondary/60 hover:shadow-xl">
              {/* Subtle Ambient Glow on Hover */}
              <div className="absolute inset-0 bg-gradient-to-b from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />

              <div>
                {/* Top Row: Stars + Verified Metric Pill */}
                <div className="flex items-center justify-between gap-3 mb-6">
                  <div role="img" className="flex gap-1" aria-label={`5 out of 5 stars rating by ${item.name}`}>
                    {[...Array(item.rating)].map((_, starIdx) => <Star key={starIdx} className="size-4 fill-secondary text-secondary" />)}
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-secondary/15 border border-secondary/30 text-secondary text-xs font-semibold">
                    <CheckCircle2 className="size-3.5 shrink-0 text-secondary" />
                    <span>{item.metric}</span>
                  </div>
                </div>

                {/* Quote Content */}
                <div className="relative mb-8">
                  <Quote className="absolute -top-2 -left-1 size-8 text-foreground/5 -z-0 pointer-events-none" />
                  <p className="relative z-10 text-foreground/80 text-base md:text-lg leading-relaxed italic">
                    "{item.quote}"
                  </p>
                </div>
              </div>

              {/* Card Footer: Author Info & Secondary Metric */}
              <div className="pt-6 border-t border-border">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={`${item.name} - ${item.role}`} loading="lazy" fetchPriority="low" decoding="async" className="size-12 rounded-full object-cover border border-white/15 grayscale group-hover:grayscale-0 transition-all duration-300" />
                    <div>
                      <h3 className="text-base font-bold text-foreground tracking-tight">
                        {item.name}
                      </h3>
                      <p className="text-xs text-secondary font-medium tracking-wide">
                        {item.role}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.property}
                      </p>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <span className="block text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                      Impact
                    </span>
                    <span className="text-xs font-bold text-foreground">
                      {item.metricDetail}
                    </span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Bottom Action / Link */}
        <div className="mt-16 text-center">
          <Link to="/CaseStudiesPage" className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-muted-foreground hover:text-secondary transition-colors">
            <span>Explore All Client Revenue Case Studies</span>
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}